import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { memoize } from 'lodash';
import {
  getNormalizedIntervals,
  dateToOffset,
  getTotalWidthFromVisibleRange,
  getInterval,
  getPixelsPerDay,
  renderInterval,
} from './helpers';
import { instanceOfDateString } from './propTypes';
import styles from './ScaleBar.module.css';
import renderIf from 'render-if';

const intervalKey = (key, { startDate, endDate, hideWeekends, fiscalMonth }) =>
  `${key}-${startDate}-${endDate}-${hideWeekends}-${fiscalMonth}`;
const getMajorInterval = memoize(getInterval, intervalKey);
const getMinorInterval = memoize(getInterval, intervalKey);

/*
 * This component renders the ticks and dates at the bottom of the ScaleBar.
 */
export default class ScaleDates extends Component {
  static propTypes = {
    startDate: instanceOfDateString.isRequired,
    endDate: instanceOfDateString.isRequired,
    visibleStartDate: instanceOfDateString.isRequired,
    visibleEndDate: instanceOfDateString.isRequired,
    visibleWidth: PropTypes.number.isRequired,
    fiscalMonth: PropTypes.number.isRequired,
    fiscalYearFormat: PropTypes.string,
    hideWeekends: PropTypes.bool.isRequired,
    timescale: PropTypes.string.isRequired,
    onScroll: PropTypes.func,
    hideMinorInterval: PropTypes.bool,
  };

  static defaultProps = {
    hideWeekends: false,
    hideMinorInterval: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      startDate,
      endDate,
      visibleStartDate,
      visibleEndDate,
      visibleWidth,
      hideWeekends,
    } = nextProps;
    const totalWidth = getTotalWidthFromVisibleRange({
      startDate,
      endDate,
      visibleStartDate,
      visibleEndDate,
      visibleWidth,
      hideWeekends,
    });
    const intervals = getNormalizedIntervals(nextProps);
    const pixelsPerDay = getPixelsPerDay({
      startDate,
      endDate,
      hideWeekends,
      width: totalWidth,
    });

    if (startDate && endDate && visibleWidth) {
      const scrollLeft = Math.round(
        dateToOffset({
          startDate,
          endDate,
          width: totalWidth,
          hideWeekends,
          date: visibleStartDate,
        })
      );
      return {
        needsScroll: Math.abs(scrollLeft - prevState.scrollLeft) > 1,
        scrollLeft,
        totalWidth,
        pixelsPerDay,
        ...intervals,
        ...nextProps,
      };
    } else {
      return {
        totalWidth,
        pixelsPerDay,
        ...intervals,
        ...nextProps,
      };
    }
  }

  state = {
    needsScroll: false,
  };

  constructor(props) {
    super(props);

    this.scaleDatesRef = React.createRef();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextState.totalWidth !== this.state.totalWidth ||
      nextState.scrollLeft !== this.state.scrollLeft ||
      nextProps.startDate !== this.props.startDate ||
      nextProps.endDate !== this.props.endDate ||
      nextProps.hideWeekends !== this.props.hideWeekends ||
      nextProps.fiscalMonth !== this.props.fiscalMonth ||
      nextProps.fiscalYearFormat !== this.props.fiscalYearFormat ||
      nextProps.timescale !== this.props.timescale
    );
  }

  componentDidMount() {
    const { startDate, endDate, visibleStartDate, hideWeekends } = this.props;
    const totalWidth = getTotalWidthFromVisibleRange(this.props);
    const scrollLeft = dateToOffset({
      startDate,
      endDate,
      width: totalWidth,
      hideWeekends,
      date: visibleStartDate,
    });
    this.scaleDatesRef.current.scrollLeft = scrollLeft;
  }

  componentDidUpdate() {
    if (this.state.needsScroll) {
      this.scaleDatesRef.current.scrollLeft = this.state.scrollLeft;
    }
  }

  onScroll = ({ target: { scrollLeft } }) => {
    if (
      this.props.onScroll &&
      Math.abs(scrollLeft - this.state.scrollLeft) > 1
    ) {
      this.props.onScroll(scrollLeft);

      // We want to make sure that the user ends up scrolled on a full day
      // interval (not halfway through a date). To accomplish this, every time
      // they scroll we start a 25ms timer; when the timer expires, we snap them
      // to the nearest whole day. New scrolls cancel previous timers so the
      // cumulative result is that we snap them to a day interval 25ms after they
      // stop scrolling.
      if (this.snapScroll) clearTimeout(this.snapScroll);
      this.snapScroll = setTimeout(() => {
        const { pixelsPerDay } = this.state;
        const remnant = scrollLeft % pixelsPerDay;
        if (remnant < pixelsPerDay / 2) {
          this.scaleDatesRef.current.scrollLeft = scrollLeft - remnant;
        } else {
          this.scaleDatesRef.current.scrollLeft =
            scrollLeft + (pixelsPerDay - remnant);
        }
      }, 25);
    }
  };

  render() {
    const { major, minor, totalWidth } = this.state;

    // getInterval expects a 1-12 based fiscal month but we're given a zero-based fiscal month
    const intervalProps = {
      ...this.props,
      fiscalMonth: this.props.fiscalMonth,
    };
    const majorInterval = getMajorInterval(major, intervalProps);
    const minorInterval = getMinorInterval(minor, intervalProps);

    const majorIntervals = renderInterval({
      intervalType: 'major',
      interval: majorInterval,
      className: classnames(styles.interval, styles.major),
    });
    const minorIntervals = renderInterval({
      intervalType: 'minor',
      interval: minorInterval,
      className: classnames(styles.interval, {
        [styles.minorDays]: minor === 'days',
      }),
    });

    return (
      <div className={styles.scaleDatesWrapper}>
        <div
          ref={this.scaleDatesRef}
          onScroll={this.onScroll}
          className={styles.scaleDates}
        >
          <div
            className={classnames([styles.intervals, styles.majorIntervals])}
            style={{ width: totalWidth }}
          >
            {majorIntervals}
          </div>

          {renderIf(!this.props.hideMinorInterval)(() => (
            <div
              className={classnames([
                styles.intervals,
                ['days', 'weeks'].includes(minor)
                  ? styles.intervalsNoBorder
                  : null,
              ])}
              style={{ width: totalWidth }}
            >
              {minorIntervals}
            </div>
          ))}
        </div>
      </div>
    );
  }
}
